/**
 * Brochure Builder — master-only admin panel (Phase 1).
 *
 * Sits inside the existing AdminDealerDetailPage as a tab. Mirrors the
 * Recall Check panel's shape so the pattern is obvious to future
 * modules.
 *
 * Master-only. Dealers don't see this — the entitlement toggle itself
 * is only reachable with MASTER_ADMIN.
 */
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { Loader2, FileText, ShieldOff, Save, ExternalLink } from "lucide-react";

const INPUT_CLS =
  "w-full rounded-md border border-gray-200 px-3 py-2 text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-gray-300";

function formatTs(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

export default function BrochureBuilderAdminPanel({ dealer }) {
  const { apiFetch } = useAuth();
  const { success: toastOk, error: toastErr } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [state, setState] = useState(null);   // server truth (as of last load)
  const [draft, setDraft] = useState({});     // unsaved edits

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch(
        `/admin/dealers/${dealer.id}/brochure/settings`, { method: "GET" });
      const data = r?.data || r;
      setState(data);
      setDraft({
        brochure_credit_app_url: data.brochure_credit_app_url || "",
        brochure_allowed_domains: data.brochure_allowed_domains || "",
      });
    } catch {
      toastErr("Could not load Brochure Builder settings.");
    } finally {
      setLoading(false);
    }
  }, [dealer.id, apiFetch, toastErr]);

  useEffect(() => { load(); }, [load]);

  const flipEnabled = async (next, opts = {}) => {
    setSaving(true);
    try {
      const r = await apiFetch(
        `/admin/dealers/${dealer.id}/brochure/settings`,
        { method: "PATCH", data: {
            enabled: next,
            ...(opts.force ? { force_enable_without_scrape_check: true } : {}),
          } });
      setState(r?.data || r);
      toastOk(next
        ? (opts.force
            ? "Enabled — hard gate overridden. Log entry recorded."
            : "Brochure Builder enabled for this dealer.")
        : "Brochure Builder disabled. Existing brochure links keep working; new ones are blocked.");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message;
      if (status === 409) {
        toastErr("Hard gate — run the Real Dealer Scrape Test first. See the yellow panel below.");
      } else {
        toastErr(detail || "Failed to update entitlement.");
      }
    } finally {
      setSaving(false);
    }
  };

  const clearScrapeVerification = async () => {
    if (!window.confirm("Clear scrape verification for this dealer? "
                        + "They'll need a fresh /preview run before Brochure Builder can be re-enabled."))
      return;
    setSaving(true);
    try {
      const r = await apiFetch(
        `/admin/dealers/${dealer.id}/brochure/settings`,
        { method: "PATCH", data: { clear_scrape_verification: true } });
      setState(r?.data || r);
      toastOk("Scrape verification cleared.");
    } catch (e) {
      toastErr(e?.response?.data?.detail || "Failed to clear.");
    } finally {
      setSaving(false);
    }
  };

  const saveFields = async () => {
    setSaving(true);
    try {
      const r = await apiFetch(
        `/admin/dealers/${dealer.id}/brochure/settings`,
        { method: "PATCH", data: draft });
      const data = r?.data || r;
      setState(data);
      setDraft({
        brochure_credit_app_url: data.brochure_credit_app_url || "",
        brochure_allowed_domains: data.brochure_allowed_domains || "",
      });
      toastOk("Brochure Builder configuration saved.");
    } catch (e) {
      toastErr(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !state) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 flex items-center gap-2 text-gray-500 text-[13px]"
           data-testid="brochure-admin-loading">
        <Loader2 className="animate-spin" size={16} /> Loading Brochure Builder…
      </div>
    );
  }

  const on = !!state.enabled;

  return (
    <div className="space-y-4" data-testid="brochure-admin-panel">
      {/* Entitlement toggle */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-[15px] font-semibold text-gray-900 flex items-center gap-2">
              {on
                ? <FileText size={16} className="text-emerald-600" />
                : <ShieldOff size={16} className="text-gray-400" />}
              Brochure Builder — à la carte product
            </h3>
            <p className="text-[12px] text-gray-500 mt-1 max-w-2xl">
              Lets this dealer&apos;s sales reps turn any used-car listing on
              their site into a personalized, trackable brochure they can
              text or email to a customer. Default is OFF for every dealer.
              Turn it on here; dealer admins grant individual reps access
              (reps use their existing dealer logins — no new user model).
            </p>
          </div>
          <button
            type="button"
            onClick={() => flipEnabled(!on)}
            disabled={saving}
            data-testid="brochure-entitlement-toggle"
            className={`shrink-0 rounded-full px-4 py-2 text-[13px] font-semibold transition-colors disabled:opacity-60 ${
              on
                ? "bg-emerald-600 text-white hover:bg-emerald-700"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            {saving ? "Saving…" : on
              ? "Enabled — click to turn off"
              : "Disabled — click to turn on"}
          </button>
        </div>

        <div
          className={`mt-3 text-[12px] rounded-md px-3 py-2 ${on ? "bg-emerald-50 text-emerald-800" : "bg-gray-50 text-gray-600"}`}
          data-testid="brochure-entitlement-state"
        >
          {on
            ? "Live for this dealer. Billing should drive this switch — turn it off when they stop paying."
            : "Not sold to this dealer. Every brochure endpoint returns 403; the nav item is hidden."}
        </div>

        <dl className="grid grid-cols-2 gap-2 mt-3 text-[11.5px] text-gray-500">
          <div>
            <dt className="font-semibold uppercase tracking-wider">Last enabled</dt>
            <dd data-testid="brochure-enabled-at">{formatTs(state.enabled_at)}</dd>
          </div>
          <div>
            <dt className="font-semibold uppercase tracking-wider">Last disabled</dt>
            <dd data-testid="brochure-disabled-at">{formatTs(state.disabled_at)}</dd>
          </div>
        </dl>
      </div>

      {/* HARD GATE — Real Dealer Scrape Test */}
      <div
        className={`rounded-lg border p-4 ${state.scrape_verified
          ? "bg-emerald-50 border-emerald-200"
          : "bg-amber-50 border-amber-200"}`}
        data-testid="brochure-scrape-verification-card"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className={`text-[13px] font-semibold flex items-center gap-2 ${state.scrape_verified ? "text-emerald-900" : "text-amber-900"}`}>
              {state.scrape_verified ? "✓ Real Dealer Scrape Test — passed" : "⚠ Real Dealer Scrape Test — not run yet"}
            </div>
            <div className={`text-[12px] mt-1 ${state.scrape_verified ? "text-emerald-800" : "text-amber-800"}`}>
              {state.scrape_verified
                ? <>Verified against a live URL at <strong>{formatTs(state.scrape_verified_at)}</strong>.
                    Brochure Builder can be enabled normally.</>
                : <>Master cannot enable Brochure Builder for a real dealer until someone has run a
                    live VDP URL through <code>POST /api/dealer/brochure/preview</code> from a rep
                    account on this dealership and confirmed the scraper returns real data (not
                    manual-only on every field). This prevents rolling the feature out to a dealer
                    whose site platform we haven&apos;t validated yet.</>}
            </div>
            {state.scrape_verified_url && (
              <div className="text-[11px] mt-1 text-emerald-700 truncate max-w-full">
                Verified URL: <code>{state.scrape_verified_url}</code>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-1.5 shrink-0">
            {!state.scrape_verified && !on && (
              <button
                onClick={() => flipEnabled(true, { force: true })}
                disabled={saving}
                className="text-[11.5px] font-semibold px-3 py-1.5 rounded border border-amber-400 bg-white text-amber-900 hover:bg-amber-100 disabled:opacity-60"
                data-testid="brochure-force-enable"
              >
                Force enable (override) →
              </button>
            )}
            {state.scrape_verified && (
              <button
                onClick={clearScrapeVerification}
                disabled={saving}
                className="text-[11.5px] font-semibold px-3 py-1.5 rounded border border-emerald-400 bg-white text-emerald-900 hover:bg-emerald-100 disabled:opacity-60"
                data-testid="brochure-clear-verification"
              >
                Clear verification (re-run required)
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Module-specific configuration */}
      <div className="bg-white rounded-lg border border-gray-200 p-5 space-y-4"
           data-testid="brochure-config-card">
        <div>
          <h3 className="text-[15px] font-semibold text-gray-900">Dealer configuration</h3>
          <p className="text-[12px] text-gray-500 mt-1">
            Applies whether the module is on or off. Saved here so the master can
            pre-configure a dealer before turning them on.
          </p>
        </div>

        <label className="block">
          <span className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
            Credit application URL
          </span>
          <input
            className={INPUT_CLS}
            data-testid="brochure-credit-app-url"
            placeholder="https://baymotors.com/apply"
            value={draft.brochure_credit_app_url || ""}
            onChange={(e) => setDraft((d) => ({ ...d, brochure_credit_app_url: e.target.value }))}
          />
          <span className="block text-[11.5px] text-gray-400 mt-1">
            Every brochure&apos;s <em>&quot;Get qualified for the best rates
            available&quot;</em> button links here. Must start with{" "}
            <code>http://</code> or <code>https://</code>.
          </span>
          {state.brochure_credit_app_url && (
            <a href={state.brochure_credit_app_url} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 text-[11.5px] text-blue-700 hover:underline mt-1"
               data-testid="brochure-credit-app-link">
              Preview current link <ExternalLink size={11} />
            </a>
          )}
        </label>

        <label className="block">
          <span className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
            Allowed listing domains
          </span>
          <input
            className={INPUT_CLS}
            data-testid="brochure-allowed-domains"
            placeholder="baymotors.com, www.baymotors.com"
            value={draft.brochure_allowed_domains || ""}
            onChange={(e) => setDraft((d) => ({ ...d, brochure_allowed_domains: e.target.value }))}
          />
          <span className="block text-[11.5px] text-gray-400 mt-1">
            Comma- or newline-separated. Reps can only paste listing URLs whose
            host matches one of these. Blank = block everything (fail-safe deny).
          </span>
        </label>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={saveFields}
            disabled={saving}
            data-testid="brochure-save-config"
            className="inline-flex items-center gap-2 rounded-md bg-gray-900 text-white text-[13px] font-semibold px-4 py-2 hover:bg-gray-800 disabled:opacity-60"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save configuration
          </button>
        </div>
      </div>
    </div>
  );
}
